import {
  Center,
  CenterFeedPlacement,
  FeedOptions,
  FeedProperty,
  ScoringData,
  FeedItemEnum,
  FeedItems
} from '@/types/models'
import { getLocalBlockLengthFromFeedLayout } from '@/domain/feed/useCases/getLocalAdBlock'
import { isNonEmptyArray } from '@/utils/checks'
import {
  addSingleItemsAtPosition,
  sortConstructedFeed
} from './addSingleItemsAtPosition'
import { singleInserts } from './constants'
import {
  addAdsTypeAndZoneId,
  addLocalFeedTypeAndZoneId,
  addNearbyFeedTypeAndZoneId,
  addOrganicFeedTypeAndZoneId
} from '../center/useCases/addFeedTypeAndZoneId'
import { createLocalCenters } from './useCases/createLocalCenters'
import { removePriorityItemsFromCenters } from './useCases/dedupeCentersByPriority'
import { AddItemsToSubFeed } from './useCases/AddItemsToSubFeed'
import { addNearbyHeader } from './useCases/addNearbyHeader'
import { getRepeatPatternSubItemCount } from './useCases/getRepeatPatternSubItemCount'

export const subAndMainFeedHaveItems = (subFeed, mainFeed) => {
  return isNonEmptyArray(subFeed) && isNonEmptyArray(mainFeed)
}

export const addComponentsToFeed = ({
  feedLayout,
  localFeed,
  organicFeed,
  nearbyFeed,
  midFeedAds,
  scoringData
}: {
  feedLayout: FeedProperty[]
  localFeed: CenterFeedPlacement[] | []
  organicFeed: CenterFeedPlacement[] | []
  nearbyFeed: CenterFeedPlacement[] | []
  midFeedAds: CenterFeedPlacement[] | []
  scoringData: ScoringData
}) => {
  let localCenters: CenterFeedPlacement[] = localFeed
  let organicCenters: CenterFeedPlacement[] = organicFeed
  let adverts: CenterFeedPlacement[] = midFeedAds
  if (getLocalBlockLengthFromFeedLayout(feedLayout)) {
    localCenters = createLocalCenters({
      organicCenters: organicCenters,
      scoringData,
      localBlockCount: getLocalBlockLengthFromFeedLayout(feedLayout)
    })
    organicCenters = removePriorityItemsFromCenters({
      listWithPriority: localCenters,
      listToRemoveFrom: organicCenters
    }) as CenterFeedPlacement[]
  }
  adverts = removePriorityItemsFromCenters({
    listWithPriority: [...organicCenters, ...localCenters],
    listToRemoveFrom: adverts
  }) as CenterFeedPlacement[]

  const initialFeed = createInitialFeed({
    feedLayout,
    localFeed: localCenters,
    organicFeed: organicCenters,
    nearbyFeed,
    midFeedAds: adverts
  })

  const constructedFeed = addSingleItemsAtPosition({
    feed: initialFeed as Partial<CenterFeedPlacement>[],
    template: feedLayout
  })
  if (nearbyFeed.length) {
    const nearbyConstructedFeed = addNearbyHeader({
      feed: constructedFeed
    })
    return sortConstructedFeed(nearbyConstructedFeed as CenterFeedPlacement[])
  }

  return sortConstructedFeed(constructedFeed as CenterFeedPlacement[])
}

export const createInitialFeed = ({
  feedLayout,
  localFeed = [],
  organicFeed = [],
  nearbyFeed = [],
  midFeedAds = []
}: {
  feedLayout: FeedProperty[]
  localFeed: Center[] | []
  organicFeed: Center[] | []
  nearbyFeed: Center[] | []
  midFeedAds: Center[] | []
}) => {
  const localItems = localFeed.map(addLocalFeedTypeAndZoneId)
  const midFeedAdsItems = midFeedAds.map(addAdsTypeAndZoneId)
  const organicItems = organicFeed.map(addOrganicFeedTypeAndZoneId)
  const nearbyItems = nearbyFeed.map(addNearbyFeedTypeAndZoneId)

  const feedItems = {
    localItems,
    midFeedAdsItems,
    organicItems,
    nearbyItems
  }

  let lastCenterStartingIndex = 0
  let indexToBeiterated = 0
  const constructedFeed = feedLayout
    .filter((item) => !singleInserts.includes(item.feedType))
    .map((item: FeedProperty) => {
      lastCenterStartingIndex += indexToBeiterated
      indexToBeiterated = 0
      const { feedType, maxItems, inFeed } = item
      if (singleInserts.includes(feedType)) {
        return []
      }
      if (item.feedType === FeedOptions.Explore) {
        return { ...item, feedPosition: lastCenterStartingIndex }
      }
      if (!feedSourceHasItems(feedType, feedItems)) {
        return []
      }
      if (inFeed?.repeatEvery) {
        const feed = mapPositionToFeedItems(
          feedItems[FeedItemEnum[feedType]].splice(0, maxItems),
          lastCenterStartingIndex
        )

        const subFeed = feedItems[FeedItemEnum[inFeed.feedSource]].splice(
          0,
          Math.floor(feed.length / inFeed?.repeatEvery)
        )

        indexToBeiterated = feed.length + subFeed.length
        return AddItemsToSubFeed(feed, subFeed, item, lastCenterStartingIndex)
      }
      if (inFeed?.repeatPattern) {
        const feed = mapPositionToFeedItems(
          feedItems[FeedItemEnum[feedType]].splice(0, maxItems),
          lastCenterStartingIndex
        )
        const subFeedSpliceLength = getRepeatPatternSubItemCount(
          inFeed.repeatPattern,
          feed.length
        )
        const subFeed = feedItems[FeedItemEnum[inFeed.feedSource]].splice(
          0,
          subFeedSpliceLength
        )
        indexToBeiterated = feed.length + subFeed.length
        return AddItemsToSubFeed(feed, subFeed, item, lastCenterStartingIndex)
      }
      const feed = mapPositionToFeedItems(
        feedItems[FeedItemEnum[feedType]].splice(0, maxItems),
        lastCenterStartingIndex
      )
      lastCenterStartingIndex += feed.length
      return feed
    })
    .flat()
    .filter(Boolean)
    .sort((a, b) => {
      return (
        ((a as { feedPosition: number }).feedPosition as number) -
        ((b as { feedPosition: number }).feedPosition as number)
      )
    })
    .map((item, index) => {
      return { ...item, feedPosition: index }
    })
  return constructedFeed
}

export const mapPositionToFeedItems = (
  feed: CenterFeedPlacement[],
  positionStart: number
) =>
  feed.map((item, index) => {
    return { ...item, feedPosition: positionStart + index }
  }) as CenterFeedPlacement[]

const feedSourceHasItems = (
  feedType: FeedProperty['feedType'],
  feedItems: FeedItems
) => {
  return isNonEmptyArray(feedItems[FeedItemEnum[feedType]])
}
